import { useEffect, useRef, useState } from 'react';
import { GdkMultipleSelectBox } from '@gdk/multiple-select-box';
import Pagination from '../SweepsAccountManagement/Pagination';
import { addToList, updateList } from '../../api/admin';
import $ from 'jquery';

const styles = {
    sectionLabel: {
        textTransform: 'uppercase',
        fontSize: '1.5rem',
        fontWeight: '600',
        color: '#555',
        letterSpacing: '0.05em',
        marginBottom: '8px',
        marginTop: '24px',
        display: 'block'
    },
    card: {
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        backgroundColor: '#fff'
    },
    checkbox: {
        width: '24px',
        height: '24px',
        cursor: 'pointer'
    },
    selectAllLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer'
    },
    producerRoleBadge: {
        marginLeft: '8px',
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '1.1rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    }
};

const PRODUCER_PAGE_SIZE = 10;

export default function DesignatedLicensedProducers({
    fetchList = () => { },
    principalId = null,
    designatedProducers,
    setDesignatedProducers,
    allApiProducers,
    agencyInfo,
    isOnListRef,
    listRecordIdRef,
    isSaveEnabled,
    setIsSaveEnabled,
    isDirtyRef,
    savedProducersRef,
    showSaveError,
    setShowSaveError,
    saveSuccessToastRef,
    onNavigateBack
}) {
    const [selectedProducerIds, setSelectedProducerIds] = useState([]);
    const [producerFilter, setProducerFilter] = useState('');
    const [producerPage, setProducerPage] = useState(1);
    const [producerViewAll, setProducerViewAll] = useState(false);
    const [showRemoveAllModal, setShowRemoveAllModal] = useState(false);
    const removeAllModalRef = useRef();

    const designatedNpns = new Set(designatedProducers.map(p => p.nationalProducerNumber));
    const availableProducers = allApiProducers.filter(a => !designatedNpns.has(a.npn));

    const filteredProducers = designatedProducers.filter(p =>
        !producerFilter ||
        p.firstName?.toLowerCase().includes(producerFilter.toLowerCase()) ||
        p.lastName?.toLowerCase().includes(producerFilter.toLowerCase()) ||
        p.nationalProducerNumber?.includes(producerFilter)
    );

    const producerTotalPages = Math.ceil(filteredProducers.length / PRODUCER_PAGE_SIZE);
    const paginatedProducers = producerViewAll
        ? filteredProducers
        : filteredProducers.slice((producerPage - 1) * PRODUCER_PAGE_SIZE, producerPage * PRODUCER_PAGE_SIZE);

    const allProducersSelected =
        filteredProducers.length > 0 &&
        filteredProducers.every(p => selectedProducerIds.includes(p.agentId));

    // Initialise GDK MultipleSelectBox once on mount (component only mounts on the DLP segment)
    useEffect(() => {
        const el = document.querySelector('#add-producer-select');
        if (!el) return;
        const instance = new GdkMultipleSelectBox({ content: '#add-producer-select' });
        return () => { instance?.destroy(); };
    }, []);

    useEffect(() => {
        const modal = removeAllModalRef.current;
        if (!modal) return;

        const handleModalClosed = () => setShowRemoveAllModal(false);
        modal.addEventListener('modal-closed', handleModalClosed);
        return () => modal.removeEventListener('modal-closed', handleModalClosed);
    }, [showRemoveAllModal]);

    // --- Handlers ---
    const handleSelectAllProducers = (checked) => {
        setSelectedProducerIds(checked ? filteredProducers.map(p => p.agentId) : []);
    };

    const handleProducerCheck = (id, checked) => {
        setSelectedProducerIds(prev =>
            checked ? [...prev, id] : prev.filter(pid => pid !== id)
        );
    };

    const handleRemoveProducer = (id) => {
        isDirtyRef.current = true;
        setIsSaveEnabled(true);
        setDesignatedProducers(prev => prev.filter(p => p.agentId !== id));
        setSelectedProducerIds(prev => prev.filter(pid => pid !== id));
    };

    const handleRemoveAllProducers = () => {
        isDirtyRef.current = true;
        setIsSaveEnabled(true);
        setDesignatedProducers([]);
        setSelectedProducerIds([]);
    };

    const handleRemoveSelectedProducers = () => {
        isDirtyRef.current = true;
        setIsSaveEnabled(true);
        const toRemove = new Set(selectedProducerIds);
        setDesignatedProducers(prev => prev.filter(p => !toRemove.has(p.agentId)));
        setSelectedProducerIds([]);
    };

    const handleAddProducers = () => {
        let selected;
        try {
            selected = $('#add-producer-select.select2-hidden-accessible').select2('data').map(d => d.id);
        } catch {
            selected = [];
        }
        const allNpns = availableProducers.map(a => a.npn);
        const isSelectAll = selected.includes('select-all');
        const npnsToAdd = isSelectAll ? allNpns : selected;
        const existingIds = new Set(designatedProducers.map(p => p.agentId));
        const toAdd = availableProducers
            .filter(a => npnsToAdd.includes(a.npn) && !existingIds.has(a.agentSob))
            .map(a => ({ agentId: a.agentSob, firstName: a.firstName, lastName: a.lastName, nationalProducerNumber: a.npn, isDefault: a.agentSob === principalId }));
        if (toAdd.length > 0) {
            isDirtyRef.current = true;
            setIsSaveEnabled(true);
            setDesignatedProducers(prev => [...prev, ...toAdd]);
        }
        if (isSelectAll) {
            $('#add-producer-select').val(allNpns).trigger('change');
        }
    };

    const handleAddAllProducers = () => {
        const existingIds = new Set(designatedProducers.map(p => p.agentId));
        const toAdd = availableProducers
            .filter(a => !existingIds.has(a.agentSob))
            .map(a => ({ agentId: a.agentSob, firstName: a.firstName, lastName: a.lastName, nationalProducerNumber: a.npn, isDefault: a.agentSob === principalId }));
        if (toAdd.length > 0) {
            isDirtyRef.current = true;
            setIsSaveEnabled(true);
            setDesignatedProducers(prev => [...prev, ...toAdd]);
        }
        $('#add-producer-select').val(availableProducers.map(a => a.npn)).trigger('change');
    };

    const executeSave = (request) => {
        const onSaveSuccess = () => {
            isDirtyRef.current = false;
            setIsSaveEnabled(false);
            savedProducersRef.current = designatedProducers;
            isOnListRef.current = true;
            setShowSaveError(false);
            saveSuccessToastRef.current?.show();
            fetchList();
        };

        const onSaveError = (error) => {
            console.error(error);
            setShowSaveError(true);
        };

        if (isOnListRef.current && listRecordIdRef.current) {
            updateList({ ...request, id: listRecordIdRef.current }, onSaveSuccess, onSaveError);
        } else {
            addToList(request, (response) => {
                listRecordIdRef.current = response?.id ?? listRecordIdRef.current;
                onSaveSuccess();
            }, onSaveError);
        }
    };

    const createRequest = (optInStatus, agents) => {
        return {
            sob: agencyInfo?.agencyId,
            name: agencyInfo?.name,
            nationalProducerNumber: agencyInfo?.npn,
            federalEmployerIdentificationNumber: agencyInfo?.fein,
            profileType: 'Agency',
            listType: 'BindingAuthority',
            listStatus: true,
            modifiedByUserId: agencyInfo?.agentId,
            modifiedDate: new Date().toISOString(),
            bindingAuthority: {
                optInStatus: optInStatus,
                configurationLevel: 'Agency',
                agents: agents,
            },
        };
    };

    const handleSave = () => {
        if (designatedProducers.length === 0) {
            setShowRemoveAllModal(true);
            return;
        }

        const request = createRequest(true, designatedProducers.map(p => ({
            agentId: p.agentId,
            isDefault: p.isDefault === true,
            assignedUnlicensedProducers: Array.isArray(p.assignedUnlicensedProducers)
                ? p.assignedUnlicensedProducers
                : []
        })));

        executeSave(request);
    };

    const handleConfirmRemoveAll = () => {
        setShowRemoveAllModal(false);
        const request = createRequest(false, []);
        executeSave(request);
    };

    const handleCancelRemoveAll = () => {
        setShowRemoveAllModal(false);
    };

    // --- Rows ---
    const producerRows = paginatedProducers.map(producer => (
        <gds-table-tr key={producer.agentId}>
            <gds-table-td>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={selectedProducerIds.includes(producer.agentId)}
                        onChange={() => handleProducerCheck(producer.agentId, !selectedProducerIds.includes(producer.agentId))}
                        style={styles.checkbox}
                    />
                    <span>
                        <strong>{producer.firstName} {producer.lastName}</strong>{' '}NPN {producer.nationalProducerNumber}
                        {producer.isDefault && (
                            <span style={styles.producerRoleBadge}>PRINCIPAL</span>
                        )}
                    </span>
                </label>
            </gds-table-td>
            <gds-table-td style={{ textAlign: 'end' }}>
                <gds-icon
                    icon-type="close"
                    color="action"
                    style={{ cursor: 'pointer' }}
                    size="small"
                    onClick={() => handleRemoveProducer(producer.agentId)}
                />
            </gds-table-td>
        </gds-table-tr>
    ));

    return (
        <>
            {showRemoveAllModal && (
                <gds-modal type="modal" open ref={removeAllModalRef}>
                    <span slot="header">Remove All Designated Licensed Producers?</span>
                    <div>
                        <p>This change will remove all CSR Supervisor assignments and CSR will not be able to bind policies. Do you want to proceed?</p>
                    </div>
                    <gds-button
                        appearance="secondary"
                        slot="buttons"
                        type="button"
                        onClick={handleCancelRemoveAll}
                    >
                        Cancel
                    </gds-button>
                    <gds-button
                        appearance="primary"
                        slot="buttons"
                        type="button"
                        onClick={handleConfirmRemoveAll}
                    >
                        Proceed
                    </gds-button>
                </gds-modal>
            )}
            {showSaveError && (
                <gds-alert>
                    <gds-alert-message type="danger">
                        Changes Not Saved{' '}
                        <span slot="content">
                            The changes you made were not saved because of a technical problem. Please try again later. If you continue to experience this issue, contact our help desk at 1-800-624-2513 for further assistance.
                        </span>
                    </gds-alert-message>
                </gds-alert>
            )}

            {/* Add Producers */}
            <span style={styles.sectionLabel}>Add Producers</span>
            <div style={styles.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                        <label htmlFor="add-producer-select" className="text">Add Producers</label>
                        <select multiple id="add-producer-select" name="add-producer-select">
                            <option value="select-all">Select All</option>
                            {availableProducers.map(agent => (
                                <option key={agent.npn} value={agent.npn}>
                                    {agent.firstName} {agent.lastName} NPN {agent.npn}
                                </option>
                            ))}
                        </select>
                    </div>
                    <gds-button appearance="primary" size="large" type="button" onClick={handleAddProducers}>Add</gds-button>
                    <gds-button appearance="secondary" size="large" type="button" onClick={handleAddAllProducers}>Add All</gds-button>
                </div>
            </div>

            {/* Designated Licensed Producers list */}
            <span style={styles.sectionLabel}>Designated Licensed Producers</span>
            <div style={styles.card}>
                <div style={{ display: 'flex', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <gds-input-wrapper size="large" style={{ display: 'block', width: '100%' }}>
                            <gds-label-legend element="label" slot="label" for="filter-producers">
                                Filter List
                            </gds-label-legend>
                            <gds-icon slot="left-visual" icon-type="filter" size="small" />
                            <input
                                slot="input"
                                type="search"
                                id="filter-producers"
                                name="filter-producers"
                                placeholder="Search..."
                                value={producerFilter}
                                onChange={e => { setProducerFilter(e.target.value); setProducerPage(1); }}
                                style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </gds-input-wrapper>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <label style={styles.selectAllLabel}>
                            <input
                                type="checkbox"
                                checked={allProducersSelected}
                                onChange={() => handleSelectAllProducers(!allProducersSelected)}
                                style={styles.checkbox}
                            />
                            <span>Select all on page</span>
                        </label>
                        {selectedProducerIds.length > 0 && (
                            <gds-button appearance="secondary" size="large" type="button" onClick={handleRemoveSelectedProducers}>
                                Remove Selected
                            </gds-button>
                        )}
                    </div>
                    {designatedProducers.length > 0 && producerRows.length > 0 && (
                        <gds-button appearance="secondary" size="large" type="button" onClick={handleRemoveAllProducers}>
                            Remove All
                        </gds-button>
                    )}
                </div>
                <gds-table>
                    <gds-table-thead>
                        <gds-table-tr>
                            <gds-table-th>Designated Licensed <br /> Producers</gds-table-th>
                            <gds-table-th style={{ textAlign: 'right' }}>Actions</gds-table-th>
                        </gds-table-tr>
                    </gds-table-thead>
                    <gds-table-tbody>
                        {producerRows.length === 0 ? (
                            <gds-table-tr>
                                <gds-table-td colSpan="2" style={{ textAlign: 'center', color: '#888' }}>
                                    No designated producers.
                                </gds-table-td>
                            </gds-table-tr>
                        ) : producerRows}
                    </gds-table-tbody>
                </gds-table>
                {filteredProducers.length > 0 && (
                    <Pagination
                        totalPages={producerTotalPages}
                        currentPage={producerPage}
                        handlePageChange={setProducerPage}
                        handleViewAll={() => setProducerViewAll(v => !v)}
                        viewAll={producerViewAll}
                    />
                )}
            </div>

            {/* Footer */}
            <div className="row margin-top" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingBottom: '40px' }}>
                <gds-button appearance="secondary" size="large" type="button" onClick={onNavigateBack}>
                    Cancel
                </gds-button>
                <gds-button
                    appearance="primary"
                    size="large"
                    type="button"
                    disabled={!isSaveEnabled || undefined}
                    onClick={handleSave}
                >
                    Save
                </gds-button>
            </div>
        </>
    );
}
